import { useDispatch, useSelector } from 'react-redux';
import { AccessoryItem } from 'views/ProductPage/accessories/accessoryItem';
import { getAccessories } from 'views/ProductPage/accessories/selector';
import { isMobile } from 'components/Shared/responsive';
import { Button } from 'components/Button';
import { hasElements, NO_ELEMENT, rm } from 'utils';
import { useIntersectionObserver, useToggle } from 'hooks';
import styles from './style.scss';
import { IconArrow } from 'components/Shared/Icon';
import { ProductSection } from 'views/ProductPage/themes/BGH/sections/productSection';
import { IRelatedProduct } from 'models/BasicProduct';
import { getAccessoryListName, mapRelatedProductToGtm } from 'store/googleTagManager/events/utils';
import { GtmSelectItem, GtmViewListItem } from 'store/googleTagManager/actions';
import { TrackedElement } from 'hooks/useIntersectionObserver';
import { RelatedProduct } from 'models/RelatedProduct';

interface Props {
  className?: string;
}

const INITIAL_ACCESSORIES_ON_MOBILE = 3;

export const Accessories = ({ className }: Props) => {
  const mobile = useSelector(isMobile);
  const accessories = useSelector(getAccessories);
  const [showAllAccessories, toggleShowAllAccessories] = useToggle(false);
  const reduxDispatch = useDispatch();

  const { setElementRef } = useIntersectionObserver({
    onBatchVisible: handleElementsVisible
  });

  function handleElementsVisible(elements: TrackedElement<RelatedProduct>[]) {
    const items = elements.map(element => {
      const viewedProduct = element.payload;
      const productIndex = accessoriesToShow.indexOf(viewedProduct);
      const listName = getAccessoryListName(viewedProduct);

      return mapRelatedProductToGtm({
        product: viewedProduct,
        listName,
        indexInList: productIndex
      });
    });

    reduxDispatch(GtmViewListItem({ gtmItemProducts: items }));
  }

  if (!hasElements(accessories)) {
    return NO_ELEMENT;
  }

  const hasMore = accessories.length > INITIAL_ACCESSORIES_ON_MOBILE;
  const showAll = !hasMore || showAllAccessories || !mobile;
  const accessoriesToShow = showAll
    ? accessories
    : accessories.slice(0, INITIAL_ACCESSORIES_ON_MOBILE);

  const onItemClick = (product: IRelatedProduct, index: number) => {
    const listName = getAccessoryListName(product);
    const gtmItemProduct = mapRelatedProductToGtm({ product, listName, indexInList: index });

    reduxDispatch(GtmSelectItem({ gtmItemProduct: gtmItemProduct }));
  };

  return (
    <ProductSection classNames={{ container: className }} title={rm('accessories_header')}>
      <ul className={styles.accessories}>
        {accessoriesToShow.map((accessory, index) => (
          <li
            ref={node => setElementRef({ ref: node, payload: accessory })}
            className={styles.accessoryWrapper}
            key={accessory.productId}
          >
            <AccessoryItem
              {...accessory}
              gtmListName={'accessory'}
              gtmOnItemClick={product => onItemClick(product, index)}
            />
          </li>
        ))}
      </ul>

      {!showAll && (
        <Button
          className={styles.showAllButton}
          appearance="tertiary"
          onClick={toggleShowAllAccessories}
        >
          {rm('accessories_show_more')}
          <IconArrow size="small" thickness="thin" />
        </Button>
      )}
    </ProductSection>
  );
};
